'use client'

import { Textarea } from '@repo/ui'

import { OrderField } from '@/components/sections/order/order-field.client'
import { briefRequired, type CreatorOfferingView, type CreatorPick } from '@/lib/creator'
import { BRIEF_MAX_CHARS } from '@/lib/order-line'
import { pluralize } from '@/lib/tiers'
import { MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * "Co mamy przygotować?" — spec §6's brief, on the line it belongs to.
 *
 * Drawn only when `briefMode` says so, like every other control in this
 * directory: a hired camera has nothing to describe, and the field is absent
 * rather than disabled. Whether it may be left empty is the same column's
 * answer, and `pickProblem` reads it — so the sentence under an empty required
 * box and the server's refusal come from one rule stated once.
 *
 * The textarea is uncontrolled in the sense that matters: `useOrderLines`
 * repaints on the same frame and writes after a pause, so a brief being typed
 * costs one round trip when the person stops rather than one per keystroke.
 */
export function OrderBriefField({
  offering,
  pick,
  lineId,
  problem,
  onPatch,
}: {
  offering: CreatorOfferingView
  pick: CreatorPick
  /** Scopes the control id: the same offering can be in the order twice. */
  lineId: string
  problem?: string | null
  onPatch: (next: Partial<CreatorPick>) => void
}) {
  const required = briefRequired(offering)
  const used = pick.brief.length
  // Ten thumbnails ordered together become ten separate jobs (`splitPerPiece`),
  // and this one brief is what each of them starts from. Saying so here is what
  // stops somebody writing ten briefs into one box — or expecting us to guess
  // which piece each paragraph was about.
  const pieces = offering.pricingUnit === 'PIECE' && offering.bookingMode === 'NONE' ? pick.qty : 1

  return (
    <OrderField
      wide
      htmlFor={`brief-${lineId}`}
      label={
        <>
          Co mamy przygotować?
          {required ? null : <span className="font-normal text-white/55"> — opcjonalnie</span>}
        </>
      }
      problem={problem}
      hint={
        pieces > 1
          ? `Ten opis trafi do każdej z ${pieces} ${pluralize(pieces, offering.unitForms)} — resztę dogadasz na czacie.`
          : 'Materiały i referencje dorzucisz na czacie po opłaceniu.'
      }
    >
      <div className="relative">
        <Textarea
          id={`brief-${lineId}`}
          value={pick.brief}
          onChange={(event) => onPatch({ brief: event.target.value })}
          maxLength={BRIEF_MAX_CHARS}
          // Three rows, not four. The box used to be the tallest thing on a
          // card that was already 700 px of form, so the one control nobody can
          // answer in a second was also the one taking the most room.
          rows={3}
          required={required}
          placeholder="Kanał, temat odcinka, styl, kolory, czego unikać — im konkretniej, tym mniej poprawek."
          className="min-h-24 rounded-2xl border-white/15 bg-black/40 pb-7 text-[0.95rem] text-white placeholder:text-white/55"
        />
        {/* Inside the box rather than under it: the counter is about what is in
            the field, and a third line of small print below the field is what
            made this block read as three separate things. */}
        {used > 0 ? (
          <span
            className={cn(
              MICRO,
              'pointer-events-none absolute right-3 bottom-2 tabular-nums text-white/45',
            )}
          >
            {used} / {BRIEF_MAX_CHARS}
          </span>
        ) : null}
      </div>
    </OrderField>
  )
}
